import * as yup from "yup";

import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, Typography, useTheme } from "@mui/material";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../../../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ConnectZohoInfoSetupFieldsPanel } from "./Panels/ConnectZohoInfoSetupFieldsPanel";
import { ConnectZohoSetupToolbar } from "./ConnectZohoSetupToolbar";
import { ConnectZohoValidationSchema } from "./validator";
import { IAppState } from "../../../../../store/reducers";
import { IConnectZoho } from "../../../../../models/AddOns/Accounting/ZohoBooks/ConnectZoho";
import { IGetListRequest } from "../../../../../models";
import { ZohoBooksService } from "../../../../../services/AddOns/Accounting/ZohoBooks/ZohoBooksService";
import { setSelectedDrawerOptionsMenu } from "../../../../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../../../../store/actions";
import { getAddOnsScreenParentName, sortDDLByLabel } from "../../../../../common/Common";
import { useNavigateToLogout } from "../../../../../common/Common";
import { yupResolver } from "@hookform/resolvers/yup";
import { NoFoundPage } from "../../../../../pages/Anonymous/NoFoundPage";

export interface IConnectZohoSetup { }
const CONNECT_ZOHO_PERMISSION_DATA_QUERY_KEY = "connect-zoho-permission-record";
const SINGLE_RECORD_DATA_QUERY_KEY = "single-data-record";

const useGetPermissionConnectZoho = (
	clientId: string,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, clientId, navigateToLogout],
		queryFn: () => {
			return ZohoBooksService.getPermissionConnectZoho(clientId, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const useGetConnectZoho = (
	clientId: string,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, clientId, navigateToLogout],
		queryFn: () => {
			return ZohoBooksService.getPermissionConnectZoho(clientId, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const useGetConnectZohoDDL = (clientId: string,) =>
	useQuery({
		queryKey: ["connect-zoho-ddl"],
		queryFn: (data) => {
			return ZohoBooksService.getConnectZohoDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});


export const ConnectZoho: React.FC<IConnectZohoSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [connectZohoId, setConnectZohoId] = useState<boolean>(false);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const isAdminUser = useSelector(
		(state: IAppState) => state.user.profile.isSuperStatus
	);
	// const { id } = useParams();

	// Fucking combine component props defines here
	const { id, ConnectZohoPageName } = useParams();

	// connectZohoPageName would be like 
	// net_suite
	// zoho_books
	// xero
	// quick_books
	// kashoo

	const [connectZohoPageName, setConnectZohoPageName] = useState<string>("");
	useEffect(() => {
		if (ConnectZohoPageName && ConnectZohoPageName.length > 0) {

			setConnectZohoPageName(ConnectZohoPageName);
			if (ConnectZohoPageName !== connectZohoPageName) {
				formMethods.reset();
			}
		}
	}, [connectZohoPageName, ConnectZohoPageName]);

	const [editRecordId, setEditRecordId] = useState(id);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const formMethods = useForm<any>({
		resolver: yupResolver(ConnectZohoValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",

	});

	const { isPending: isPendingConnectZohoDDL, data: connectZohoDDL } =
		useGetConnectZohoDDL(clientId);

	const { isPending: isPendingConnectZohoPermision, data: zohoConnectionPermission } = useGetPermissionConnectZoho(
		clientId,
		navigateToLogout
	);

	const { isPending: isPendingConnectZohoById, data: selectedDataRecord } = useGetConnectZoho(
		clientId,
		navigateToLogout
	);

	const { isPending: isSubmitting, mutate: submitConnectZohoFormFields } =
		useMutation({
			mutationFn: ZohoBooksService.create,
			onSuccess: (res: IConnectZoho | IServerError | IResponse | any) => {

				if (res.status === 200) {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					queryClient.invalidateQueries({
						queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					});

					let path = `/add-ons/connect-zoho/${ConnectZohoPageName}/${res.data.clientId}`;
					navigate(path);
				} else if (res.status === 201) {

					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					queryClient.invalidateQueries({
						queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					});

					setEditRecordId(res.data.clientId)
					let path = `/add-ons/connect-zoho/${ConnectZohoPageName}/${res.data.clientId}`;
					navigate(path);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: (err) => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});


	const onSubmit = (values: any) => {

		let payload = {
			clientId: clientId,
			id: id && id,
			navigateToLogout: navigateToLogout,
			organization: values.organization,
		};
		submitConnectZohoFormFields(payload);
	};

	useEffect(() => {
		if (clientId && selectedDataRecord) {

			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				if (fieldName === "clientId") {
					let path = `/settings/general/setup/${clientId}`
					navigate(path)
				}
			});
		}
	}, [clientId, selectedDataRecord]);

	const { isPending: isDeleting, mutate: deleteConnectZoho } = useMutation({
		mutationFn: ZohoBooksService.delConnectZoho,

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {

				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/add-ons/connect-zoho/${ConnectZohoPageName}`;
			navigate(path);

		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const deleteConnectZohoField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				clientId: clientId,
				accountSettingsPageName: connectZohoPageName,
				navigateToLogout: navigateToLogout,
			};
			deleteConnectZoho(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setConnectZohoId(selectedDataRecord.id);
		}
	};
	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	const expandedLabels = ["Add-Ons", `${getAddOnsScreenParentName(connectZohoPageName)}`];
	const expandedAddOnsOrListItem = "Connect Zoho";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (parentLabel === `${getAddOnsScreenParentName(connectZohoPageName)}` && item.label === expandedAddOnsOrListItem) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: isExpanded,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: parentLabel === `${getAddOnsScreenParentName(connectZohoPageName)}` && parentLabel === `${getAddOnsScreenParentName(connectZohoPageName)}` && item.label === expandedAddOnsOrListItem && `/add-ons/connect-zoho/${ConnectZohoPageName}`
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	let acceptableConnectZohoPages = ["zoho_books", "zoho_people", "hr_zoho_people", "st_zoho_books"]

	return (
		<>
			{ConnectZohoPageName && acceptableConnectZohoPages.includes(ConnectZohoPageName) ?
				<Grid>
					<BackdropCircularProgress
						open={isSubmitting || isPendingConnectZohoById || isDeleting || isPendingConnectZohoDDL || isPendingConnectZohoPermision}
						testId="connect-zoho"
					/>
					<DeleteConfirmationDialog
						open={openDialog}
						deleteActionHandler={deleteConnectZohoField}
						cancelActionHandler={onCloseHandler}
						testId="connect-zoho-confirmation-dialog"
					/>

					{zohoConnectionPermission && (
						!zohoConnectionPermission.token ? (
							<>
								<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
									<Grid size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }} sx={{ background: "#ffffff", padding: "2rem", mt: 2, alignItems: "center", textAlign: "center", fontSize: ".875rem", border: "1px solid rgba(0, 0, 0, 0.12)", borderRadius: "2px" }}>
										<Link
											to={`#`}
											className="no-underline"
											style={{ color: theme.palette.primary.light }}
										>
											Click here
										</Link>
										&nbsp;&nbsp;
										<span style={{ color: "#FF6600", fontSize: ".875rem" }}>
											to allow SimplePay to access your Organization(s).
										</span>
									</Grid>
								</Grid>
							</>
						) : (
							<FormProvider {...formMethods}>
								<form onSubmit={formMethods.handleSubmit(onSubmit)}>
									<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
										<Grid size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}>
											<SingleColumnLayout
												children={
													<StandardPanel
														title="Select Default Zoho Organization"
														testId={`connect-zoho`}
														variant="outlined"
														topPadding={2}
													>
														<ConnectZohoInfoSetupFieldsPanel
															connectZohoDDL={connectZohoDDL && sortDDLByLabel(connectZohoDDL)}
														/>
													</StandardPanel>
												}
												toolbar={
													<ConnectZohoSetupToolbar
														onDeleteClicked={onDeleteClicked}
														isSubmitting={false}
													/>
												}
												testId="form-layout"
												showFullDiaplay={true}
												toolbarPosition="bottom"
											/>
										</Grid>
									</Grid>
								</form>
							</FormProvider>
						)
					)}

					<EnhancedSnackbar
						message={snackbarData.message}
						onCloseHandler={onSnackbarClosed}
						severity={snackbarData.severity}
						testId={"connect-zoho-snackbar"}
						open={snackbarData.open}
					/>
				</Grid>
				: <NoFoundPage />}</>
	);
};
